import { Link } from "react-router-dom";
import StatusBadge from "@/components/StatusBadge";
import TestResultBadge from "@/components/TestResultBadge";
import { FileText, Pencil, Trash2, RotateCcw, Lock, Unlock, Copy } from "lucide-react";

function CardField({ label, children, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] subtitle uppercase tracking-[0.2em] text-[#50493b] mb-0.5">{label}</div>
      <div className="text-[#33424f] truncate">{children}</div>
    </div>
  );
}

export const TestCard = ({ test: t, canWrite, canDelete, canUnlock, onRowOpen, onDelete, onRestore, onUnlock }) => (
  <div
    data-testid={`test-card-${t.id}`}
    onClick={() => onRowOpen(t)}
    role="button"
    tabIndex={0}
    className={`p-4 active:bg-[#f5f1ec] select-none ${t.deleted_at ? "opacity-50 line-through" : ""}`}
  >
    <div className="flex items-start justify-between gap-3 mb-3">
      <StatusBadge status={t.status} size="sm" />
      <div className="text-right">
        <div className="text-[10px] subtitle uppercase tracking-[0.2em] text-[#50493b]">Job</div>
        <div className="text-sm subtitle text-[#33424f] truncate max-w-[160px]">{t.job_number}</div>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm font-body">
      <CardField label="Vessel">{t.vessel_type}{t.vessel_number ? ` · ${t.vessel_number}` : ""}</CardField>
      <CardField label="Result"><TestResultBadge value={t.test_result} /></CardField>
      <CardField label="Test Pressure">{t.test_pressure || "—"}</CardField>
      <CardField label="Test Medium">{t.test_medium || "—"}</CardField>
      <CardField label="Test Date" className="col-span-2">{t.test_date || "—"}</CardField>
    </div>
    <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-[#33424f]/10" onClick={(e) => e.stopPropagation()}>
      <Link
        data-testid={`certificate-card-link-${t.id}`}
        to={`/tests/${t.id}/certificate`}
        className="px-3 py-2 hover:bg-[#e6e0db] text-[#33424f] inline-flex items-center gap-1.5 text-xs subtitle uppercase tracking-[0.15em] border border-[#33424f]/20"
        title="Certificate"
      >
        <FileText className="w-3.5 h-3.5" /> Cert
      </Link>
      {t.deleted_at ? null : t.blown_out_employee_id ? (
        <>
          <span data-testid={`lock-card-${t.id}`} className="px-3 py-2 text-[#33424f] inline-flex items-center gap-1.5 text-xs subtitle uppercase tracking-[0.15em] border border-[#33424f]/20" title={`Locked — Complete ${t.blown_out_date || ""}`}>
            <Lock className="w-3.5 h-3.5" /> Locked
          </span>
          {canUnlock && (
            <button
              data-testid={`unlock-card-${t.id}`}
              onClick={() => onUnlock(t.id)}
              className="px-3 py-2 hover:bg-[#f5ead0] text-[#8a6d00] inline-flex items-center gap-1.5 text-xs subtitle uppercase tracking-[0.15em] border border-[#8a6d00]/30"
              title="Unlock record (admin)"
            >
              <Unlock className="w-3.5 h-3.5" /> Unlock
            </button>
          )}
          {canWrite && (
            <Link
              data-testid={`retest-card-link-${t.id}`}
              to={`/tests/new?clone_from=${t.id}`}
              className="px-3 py-2 hover:bg-[#e6f3ff] text-[#39678f] inline-flex items-center gap-1.5 text-xs subtitle uppercase tracking-[0.15em] border border-[#39678f]/30"
              title="Re-test"
            >
              <Copy className="w-3.5 h-3.5" /> Re-test
            </Link>
          )}
        </>
      ) : (
        canWrite && (
          <Link
            data-testid={`edit-card-${t.id}`}
            to={`/tests/${t.id}`}
            className="px-3 py-2 hover:bg-[#e6e0db] text-[#33424f] inline-flex items-center gap-1.5 text-xs subtitle uppercase tracking-[0.15em] border border-[#33424f]/20"
            title="Edit"
          >
            <Pencil className="w-3.5 h-3.5" /> Edit
          </Link>
        )
      )}
      {canDelete && !t.deleted_at && !t.blown_out_employee_id && (
        <button
          data-testid={`delete-card-${t.id}`}
          onClick={() => onDelete(t.id)}
          className="px-3 py-2 hover:bg-[#fbe4ea] text-[#b30035] inline-flex items-center gap-1.5 text-xs subtitle uppercase tracking-[0.15em] border border-[#b30035]/30"
          title="Soft-delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
      {canDelete && t.deleted_at && (
        <button
          data-testid={`restore-card-${t.id}`}
          onClick={() => onRestore(t.id)}
          className="px-3 py-2 hover:bg-[#e8f6e2] text-[#2d5e1f] inline-flex items-center gap-1.5 text-xs subtitle uppercase tracking-[0.15em] border border-[#2d5e1f]/30"
          title="Restore"
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  </div>
);
